/**
 * WhoWillControlPanel — MC/SuperAdmin control surface for Who Will.
 * READY -> VOTING_OPEN -> VOTING_CLOSED -> RESULTS_REVEALED -> READY (next) -> ... -> COMPLETE
 */
import { useEffect, useState, useCallback } from "react";
import api, { formatErr } from "../lib/api";
import { useEventSocket } from "../lib/ws";
import { useMotif } from "../contexts/MotifContext";
import { toast } from "sonner";
import { Play, Pause, SkipForward, SkipBack, RotateCcw, Square, Plus, Trash2, Edit3, GripVertical, Check } from "lucide-react";
import { T } from "../lib/i18n";

export default function WhoWillControlPanel() {
  const { eventId } = useMotif();
  const [session, setSession] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [newQ, setNewQ] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [confirmStart, setConfirmStart] = useState(false);
  const [confirmRestart, setConfirmRestart] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [s, q] = await Promise.all([
        api.get("/who-will/session"),
        api.get("/who-will/questions"),
      ]);
      setSession(s.data);
      setQuestions(q.data);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEventSocket(eventId, (msg) => {
    if (msg.type === "whowill_update") refresh();
  });

  const call = async (path, verb, body) => {
    try {
      const { data } = await (body ? api.post(path, body) : api.post(path));
      toast.success(`${verb}`);
      setSession(data);
      refresh();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const startGame = () => { setConfirmStart(false); call("/who-will/session/start", "Who Will is live — MC Show is in presentation mode", {}); };
  const openVoting = () => call("/who-will/session/open-voting", "Voting open");
  const closeVoting = () => call("/who-will/session/close-voting", "Voting closed");
  const nextQ = () => call("/who-will/session/next", "Next question");
  const prevQ = () => call("/who-will/session/prev", "Previous question (votes reset)");
  const restartQ = () => { setConfirmRestart(false); call("/who-will/session/restart-question", "Question restarted at zero votes"); };
  const endGame = () => call("/who-will/session/end", "Game ended — MC Show back to Live Feed");

  const addQuestion = async () => {
    if (!newQ.trim()) return;
    try {
      await api.post("/who-will/questions", { text: newQ.trim() });
      setNewQ("");
      toast.success("Question added");
      refresh();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const saveEdit = async () => {
    if (!editingId) return;
    try {
      await api.put(`/who-will/questions/${editingId}`, { text: editText });
      setEditingId(null);
      setEditText("");
      toast.success("Question updated");
      refresh();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const remove = async (qid) => {
    if (!window.confirm("Delete this question?")) return;
    try {
      await api.delete(`/who-will/questions/${qid}`);
      toast.success("Deleted");
      refresh();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const reorder = async (qid, delta) => {
    const idx = questions.findIndex((q) => q.id === qid);
    if (idx < 0) return;
    const swap = idx + delta;
    if (swap < 0 || swap >= questions.length) return;
    const a = questions[idx], b = questions[swap];
    try {
      await Promise.all([
        api.put(`/who-will/questions/${a.id}`, { order: b.order }),
        api.put(`/who-will/questions/${b.id}`, { order: a.order }),
      ]);
      refresh();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const state = session?.state || "NOT_STARTED";
  const isActive = !["NOT_STARTED", "COMPLETE"].includes(state);
  const couple = session?.couple || { aimee: "AIMEE", riyaz: "RIYAZ" };
  const q = session?.current_question;
  const t = session?.tallies || { aimee: 0, riyaz: 0, total: 0 };
  const qIdx = q ? questions.findIndex((x) => x.id === q.id) : -1;

  return (
    <div className="space-y-4" data-testid="mc-whowill-panel">
      {/* Live control */}
      <div className="card-elev p-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="font-display text-lg text-motif">{T("Who Will · Live Control")}</div>
          {qIdx >= 0 && (
            <span className="text-[10px] text-motif-muted">Q{qIdx + 1}/{questions.length}</span>
          )}
          <span className="ml-auto text-[10px] tracking-luxe px-2 py-0.5 rounded-full" style={{
            background: "var(--motif-accent)", color: "var(--motif-primary)",
          }} data-testid="whowill-state-badge">{state}</span>
        </div>

        {q ? (
          <>
            <div className="text-[11px] tracking-luxe text-motif-muted">{T("CURRENT QUESTION")}</div>
            <div className="font-display text-lg text-motif mb-3">{q.text}</div>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <TallyCard label={couple.aimee} count={t.aimee} pct={t.total ? t.aimee / t.total * 100 : 0} testid="mc-tally-aimee" />
              <TallyCard label={couple.riyaz} count={t.riyaz} pct={t.total ? t.riyaz / t.total * 100 : 0} testid="mc-tally-riyaz" />
            </div>
            <div className="text-[11px] text-motif-muted mb-3">
              Total votes: <span className="font-display text-motif">{t.total}</span>
              <span className="ml-3">
                Results: <span className="text-motif">{state === "VOTING_CLOSED" ? "final (voting locked)" : "live to guests"}</span>
              </span>
            </div>
          </>
        ) : (
          <div className="text-motif-muted text-sm mb-3">{T("No active question. Start the game to begin.")}</div>
        )}

        <div className="flex flex-wrap gap-2">
          {!isActive && (
            <button onClick={() => setConfirmStart(true)} className="btn-primary text-sm h-10 px-4 flex items-center gap-2" data-testid="whowill-start">
              <Play className="w-4 h-4" /> START WHO WILL
            </button>
          )}
          {isActive && state === "READY" && (
            <button onClick={openVoting} className="btn-primary text-xs flex items-center gap-1" data-testid="whowill-open">
              <Play className="w-3 h-3" /> Open Poll
            </button>
          )}
          {state === "VOTING_OPEN" && (
            <button onClick={closeVoting} className="btn-primary text-xs flex items-center gap-1" data-testid="whowill-close">
              <Pause className="w-3 h-3" /> Close Poll
            </button>
          )}
          {state === "VOTING_CLOSED" && (
            <button onClick={nextQ} className="btn-ghost text-xs flex items-center gap-1" data-testid="whowill-next">
              <SkipForward className="w-3 h-3" /> Next Question
            </button>
          )}
          {isActive && qIdx > 0 && (
            <button onClick={prevQ} className="btn-ghost text-xs flex items-center gap-1" data-testid="whowill-prev">
              <SkipBack className="w-3 h-3" /> Previous
            </button>
          )}
          {isActive && q && (
            <button onClick={() => setConfirmRestart(true)} className="btn-ghost text-xs flex items-center gap-1" data-testid="whowill-restart">
              <RotateCcw className="w-3 h-3" /> Restart Question
            </button>
          )}
          {isActive && (
            <button onClick={endGame} className="btn-ghost text-xs flex items-center gap-1 ml-auto" data-testid="whowill-end">
              <Square className="w-3 h-3" /> End Game
            </button>
          )}
        </div>
      </div>

      {/* Confirmation modals */}
      {confirmStart && (
        <ConfirmModal
          title="Start Who Will?"
          message="Start the Who Will game and switch MC Show into presentation mode?"
          confirmLabel="Start Game"
          onConfirm={startGame}
          onCancel={() => setConfirmStart(false)}
          testid="whowill-confirm-start"
        />
      )}
      {confirmRestart && (
        <ConfirmModal
          title="Restart this question?"
          message="All votes for the current question will be cleared and the poll returns to READY."
          confirmLabel="Restart at Zero"
          onConfirm={restartQ}
          onCancel={() => setConfirmRestart(false)}
          testid="whowill-confirm-restart"
        />
      )}

      {/* Question manager */}
      <div className="card-elev p-4">
        <div className="font-display text-base text-motif mb-3">{T("Question Set")}</div>
        <div className="flex gap-2 mb-3">
          <input
            className="input-motif flex-1"
            placeholder="Add a new question (e.g. Who will pick the movie?)"
            value={newQ}
            onChange={(e) => setNewQ(e.target.value)}
            data-testid="whowill-new-question"
          />
          <button onClick={addQuestion} className="btn-primary text-xs flex items-center gap-1" data-testid="whowill-add">
            <Plus className="w-3 h-3" /> Add
          </button>
        </div>
        <ul className="space-y-1.5">
          {questions.map((qq, idx) => (
            <li key={qq.id} className="p-2 rounded-lg bg-accent-motif flex items-center gap-2" data-testid={`whowill-q-${qq.id}`}>
              <GripVertical className="w-3.5 h-3.5 text-motif-muted flex-shrink-0" />
              {editingId === qq.id ? (
                <input
                  className="input-motif flex-1"
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  autoFocus
                  data-testid={`whowill-edit-input-${qq.id}`}
                />
              ) : (
                <div className="flex-1 text-sm text-motif truncate">{idx + 1}. {qq.text}</div>
              )}
              <div className="flex gap-1 flex-shrink-0">
                <button onClick={() => reorder(qq.id, -1)} className="btn-ghost text-[10px] px-1" title="Move up">↑</button>
                <button onClick={() => reorder(qq.id, +1)} className="btn-ghost text-[10px] px-1" title="Move down">↓</button>
                {editingId === qq.id ? (
                  <button onClick={saveEdit} className="btn-primary text-[10px] px-2" data-testid={`whowill-save-${qq.id}`}>
                    <Check className="w-3 h-3" />
                  </button>
                ) : (
                  <button onClick={() => { setEditingId(qq.id); setEditText(qq.text); }} className="btn-ghost text-[10px] px-1" data-testid={`whowill-edit-${qq.id}`}>
                    <Edit3 className="w-3 h-3" />
                  </button>
                )}
                <button onClick={() => remove(qq.id)} className="btn-ghost text-[10px] px-1" data-testid={`whowill-delete-${qq.id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </li>
          ))}
          {questions.length === 0 && <li className="text-motif-muted italic text-xs">{T("No questions yet.")}</li>}
        </ul>
      </div>
    </div>
  );
}

export function ConfirmModal({ title, message, confirmLabel, onConfirm, onCancel, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4" data-testid={testid}>
      <div className="card-elev p-5 w-full max-w-sm">
        <div className="font-display text-lg text-motif mb-1">{title}</div>
        <p className="text-sm text-motif-muted mb-4 leading-snug">{message}</p>
        <div className="flex gap-2">
          <button onClick={onCancel} className="btn-ghost text-sm flex-1" data-testid={`${testid}-cancel`}>{T("Cancel")}</button>
          <button onClick={onConfirm} className="btn-primary text-sm flex-1" data-testid={`${testid}-confirm`}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

function TallyCard({ label, count, pct, testid }) {
  return (
    <div className="p-3 rounded-xl bg-accent-motif" data-testid={testid}>
      <div className="text-[10px] tracking-luxe text-motif-muted">{label}</div>
      <div className="flex items-baseline gap-2">
        <div className="font-display text-2xl text-motif tabular-nums">{count}</div>
        <div className="text-primary-motif text-sm tabular-nums">{Math.round(pct)}%</div>
      </div>
      <div className="h-1.5 bg-black/10 rounded-full overflow-hidden mt-1">
        <div className="h-full transition-all" style={{ width: `${pct}%`, background: "var(--motif-primary)" }} />
      </div>
    </div>
  );
}
